import { Link as RouterLink, useNavigate, useOutletContext, useParams } from 'react-router'
import { Box, Breadcrumbs, Link, Typography } from '@mui/material'
import { AdminPageHeader } from '../../components/AdminUi.js'
import {
  isOrganizationSection,
  ORGANIZATION_SECTIONS,
  OrganizationManager,
} from '../../components/OrganizationManager.js'
import { useAuth } from '../../auth/AuthContext.js'
import { visibleOrgSections, type AdminOutletContext } from './AdminLayout.js'

/**
 * Detailansicht einer Organisation im Einstellungsbereich — zeigt den über die
 * Route organizations/:orgId/:section gewählten Unterbereich (Allgemein,
 * Mitglieder, Gruppen, MCP-Server, API-Schlüssel, Microsoft Teams, Slack);
 * die Navigation dazu hängt im Menü unter der Organisation (AdminLayout).
 * Nicht freigeschaltete Unterbereiche verhalten sich für Org-Admins wie
 * nicht vorhanden (gleiches Muster wie die 404-Semantik des Servers).
 */
export const AdminOrganizationDetail = () => {
  const { orgId, section } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { organizations, organizationsLoaded, reloadOrganizations } =
    useOutletContext<AdminOutletContext>()

  const isSystemAdmin = user?.role === 'system-admin'

  if (!organizationsLoaded) return null
  const org = organizations.find((entry) => entry.id === orgId)
  const sectionAllowed =
    org !== undefined &&
    section !== undefined &&
    isOrganizationSection(section) &&
    visibleOrgSections(org, isSystemAdmin).some((entry) => entry.path === section)
  if (!org || !orgId || !section || !isOrganizationSection(section) || !sectionAllowed) {
    return <Typography color="text.secondary">Organisation nicht gefunden.</Typography>
  }
  const sectionLabel = ORGANIZATION_SECTIONS.find((entry) => entry.path === section)?.label

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Box>
        <Breadcrumbs sx={{ mb: 1.5, '& .MuiBreadcrumbs-li': { fontSize: '0.875rem' } }}>
          <Link
            component={RouterLink}
            to="/settings/organizations"
            underline="hover"
            color="inherit"
          >
            Organisationen
          </Link>
          <Link
            component={RouterLink}
            to={`/settings/organizations/${orgId}/general`}
            underline="hover"
            color="inherit"
          >
            {org.name}
          </Link>
          <Typography color="text.primary" fontSize="inherit">
            {sectionLabel}
          </Typography>
        </Breadcrumbs>
        <AdminPageHeader title={org.name} description={sectionLabel} />
      </Box>

      <OrganizationManager
        orgId={orgId}
        orgName={org.name}
        section={section}
        // Wer diese Ansicht erreicht, ist Systemadmin oder Owner/Admin der
        // Organisation (AdminLayout lädt nur eigene Admin-Orgs).
        canManage
        canDelete={isSystemAdmin}
        canManageFeatures={isSystemAdmin}
        enabledFeatures={org.enabledFeatures}
        onRenamed={() => reloadOrganizations()}
        onFeaturesChanged={() => reloadOrganizations()}
        onDeleted={() => {
          reloadOrganizations()
          navigate('/settings/organizations')
        }}
      />
    </Box>
  )
}
